import { DeleteAccount } from "@/components/DeleteAccount";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Lock } from "lucide-react";
import api, { formatApiError, planSaving } from "@/lib/api";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const Status = ({ b }) => {
  const paid = b.access_until && new Date(b.access_until) > new Date();
  return (
    <div className={`panel p-5 flex flex-wrap items-center gap-4 ${b.active ? "" : "border-rose-500/40"}`} data-testid="billing-status">
      {b.active ? <CheckCircle2 className="h-6 w-6 text-emerald-400" /> : <Lock className="h-6 w-6 text-rose-400" />}
      <div>
        <div className="text-white font-semibold" data-testid="billing-status-title">
          {b.admin ? "Admin account: full access" : paid ? "Active plan" : b.in_trial ? "Free trial active" : "Your free trial has ended"}
        </div>
        <div className="text-sm text-slate-400 num">
          {b.admin ? "No plan required." : paid ? `Access until ${fmtDate(b.access_until)}` : b.in_trial ? `7-day free trial ends ${fmtDate(b.trial_ends_at)}` : "Choose a plan below to unlock Strategic Ratings."}
        </div>
      </div>
    </div>
  );
};

const Plan = ({ p, onBuy, busy, saving }) => (
  <div className={`panel p-6 flex flex-col gap-4 ${saving ? "border-emerald-500/50" : ""}`} data-testid={`plan-${p.id}`}>
    <div className="flex items-center justify-between">
      <div className="eyebrow">{p.label}</div>
      {saving > 0 && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid={`plan-best-${p.id}`}>Best value</span>}
    </div>
    <div className="font-display text-5xl font-bold text-white">${p.amount.toFixed(2)}</div>
    <div className="text-xs text-slate-400 num">AUD • {p.days} days access • ${(p.amount / p.days).toFixed(2)}/day</div>
    {saving > 0 && <div className="text-sm text-emerald-300 font-semibold" data-testid={`plan-saving-${p.id}`}>Save ${saving} vs monthly</div>}
    <Button onClick={() => onBuy(p.id)} disabled={busy} className="mt-auto" data-testid={`plan-buy-${p.id}`}>
      <CreditCard className="h-4 w-4 mr-2" />{busy === p.id ? "Opening checkout..." : "Choose plan"}
    </Button>
  </div>
);

export default function Subscribe() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/billing/me").then((r) => setB(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const buy = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setBusy(null);
      toast.error(formatApiError(e));
    }
  };

  if (!b) return <div className="py-20 grid place-items-center"><div className="loader-bar" /></div>;
  return (
    <div className="space-y-6">
      <div className="fade-up">
        <div className="eyebrow mb-2">Subscription • prices in AUD</div>
        <h1 className="text-4xl sm:text-5xl font-bold" data-testid="subscribe-title">Choose your plan</h1>
        <p className="text-slate-400 mt-1 text-sm">7-day free trial for every new account, no card needed. Then pick a pass: pay once, no auto-renew.</p>
      </div>
      <Status b={b} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {b.plans.map((p) => <Plan key={p.id} p={p} onBuy={buy} busy={busy} saving={planSaving(b.plans, p)} />)}
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-2"><Lock className="h-3.5 w-3.5" />Add your payment method securely on the Stripe checkout page (card, Apple Pay or Google Pay). Buying again adds the time on top of any remaining access. Have a promo code? Enter it on the checkout page.</p>
      <DeleteAccount />
    </div>
  );
}
